'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import {  } from 'lucide-react';
import Spinner from '@/components/Spinner';

interface AuthUser {
  id: string;
  email: string;
  fullName?: string;
  role?: string;
  roleName?: string;
  permissions?: string[];
}

interface SessionMeta {
  lastLoginAt: string | null;
  lastFailedLoginAt: string | null;
  passwordExpiresInDays: number | null;
}

interface AuthContextType {
  user: AuthUser | null;
  sessionMeta: SessionMeta;
  loading: boolean;
  signUp: (email: string, password: string, metadata?: { fullName?: string }) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  dismissLogoutOverlay: () => void;
  getCurrentUser: () => Promise<AuthUser | null>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<any>;
  refreshSessionMeta: () => Promise<void>;
}

const DEFAULT_META: SessionMeta = { lastLoginAt: null, lastFailedLoginAt: null, passwordExpiresInDays: null };

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [sessionMeta, setSessionMeta] = useState<SessionMeta>(DEFAULT_META);
  const [loading, setLoading] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const fetchMe = async () => {
    const res = await fetch('/api/auth/me');
    if (!res.ok) return null;
    return res.json();
  };

  const refreshSessionMeta = async () => {
    const data = await fetchMe();
    if (data?.id) {
      setSessionMeta({
        lastLoginAt: data.lastLoginAt || null,
        lastFailedLoginAt: data.lastFailedLoginAt || null,
        passwordExpiresInDays: data.passwordExpiresInDays ?? null,
      });
    }
  };

  useEffect(() => {
    fetchMe()
      .then(data => {
        if (data?.id) {
          setUser({ id: data.id, email: data.email, fullName: data.fullName, role: data.role, roleName: data.roleName, permissions: data.permissions || [] });
          setSessionMeta({
            lastLoginAt: data.lastLoginAt || null,
            lastFailedLoginAt: data.lastFailedLoginAt || null,
            passwordExpiresInDays: data.passwordExpiresInDays ?? null,
          });
        } else {
          setUser(null);
          setSessionMeta(DEFAULT_META);
        }
      })
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  const signUp = async (email: string, password: string, metadata: { fullName?: string } = {}) => {
    const res = await fetch('/api/auth/signup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, fullName: metadata.fullName || '' }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Sign up failed');
    setUser({ id: data.user.id, email: data.user.email, fullName: data.user.fullName, role: data.user.role, roleName: data.user.roleName, permissions: data.user.permissions || [] });
    return data;
  };

  const signIn = async (email: string, password: string) => {
    const res = await fetch('/api/auth/signin', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Sign in failed');
    // 2FA required — don't set user yet; caller handles the TOTP step
    if (data.requiresTwoFactor) return data;
    // 2FA not yet configured — caller will force the setup flow
    if (data.requiresTwoFactorSetup) return data;
    setUser({ id: data.user.id, email: data.user.email, fullName: data.user.fullName, role: data.user.role, roleName: data.user.roleName, permissions: data.user.permissions || [] });
    return data;
  };

  const completeLogin = (data: any) => {
    if (data?.user) {
      setUser({ id: data.user.id, email: data.user.email, fullName: data.user.fullName, role: data.user.role, roleName: data.user.roleName, permissions: data.user.permissions || [] });
    }
  };

  const signOut = async () => {
    setIsLoggingOut(true);
    try {
      await fetch('/api/auth/signout', { method: 'POST' });
    } finally {
      setUser(null);
      setSessionMeta(DEFAULT_META);
      // Overlay stays until the login page calls dismissLogoutOverlay() on mount
    }
  };

  const dismissLogoutOverlay = () => setIsLoggingOut(false);

  const getCurrentUser = async (): Promise<AuthUser | null> => {
    const res = await fetch('/api/auth/me');
    if (!res.ok) return null;
    const data = await res.json();
    return data?.id ? { id: data.id, email: data.email, fullName: data.fullName } : null;
  };

  const isEmailVerified = () => true; // No email verification with direct PostgreSQL

  const getUserProfile = async () => {
    const res = await fetch('/api/profile');
    if (!res.ok) return null;
    return res.json();
  };

  const value: AuthContextType = {
    user,
    sessionMeta,
    loading,
    signUp,
    signIn,
    signOut,
    dismissLogoutOverlay,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    refreshSessionMeta,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}

      {/* Full-page logout overlay */}
      {isLoggingOut && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-white">
          <style>{`
            @keyframes logoutFadeIn {
              from { opacity: 0; transform: scale(0.96); }
              to   { opacity: 1; transform: scale(1); }
            }
            .logout-card { animation: logoutFadeIn 0.25s ease-out forwards; }
          `}</style>
          <div className="logout-card flex flex-col items-center gap-5 px-10 py-10 rounded-2xl">
            {/* Brand mark */}
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-lg">
              <svg width="28" height="28" viewBox="0 0 32 32" fill="none">
                <rect x="2" y="2" width="13" height="13" rx="3" fill="white" fillOpacity="0.9"/>
                <rect x="17" y="2" width="13" height="13" rx="3" fill="white" fillOpacity="0.5"/>
                <rect x="2" y="17" width="13" height="13" rx="3" fill="white" fillOpacity="0.5"/>
                <rect x="17" y="17" width="13" height="13" rx="3" fill="white" fillOpacity="0.9"/>
              </svg>
            </div>

            {/* Spinner + message */}
            <div className="flex flex-col items-center gap-2">
              <Spinner size={28} className="text-indigo-500" />
              <p className="text-base font-semibold text-gray-800">Logging off…</p>
              <p className="text-sm text-gray-400">See you next time{user?.fullName ? `, ${user.fullName.split(' ')[0]}` : ''}!</p>
            </div>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
};
